import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

export default class PaginatedList extends Component {
  pages = new Map();
  infinityScrollObserver;
  #resolveNextPagePromise = null;
  #resolvePreviousPagePromise = null;
  #imageRatioSetting = null;

  connectedCallback() {
    super.connectedCallback();
    if (!this.refs.viewMoreNext) return;
    this.#storeImageRatioSettings();
    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #storeImageRatioSettings() {
    const firstCardGallery = this.querySelector('[ref="cardGallery"]');
    if (!firstCardGallery) return;
    this.#imageRatioSetting = firstCardGallery.getAttribute('data-image-ratio');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) this.infinityScrollObserver.disconnect();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    if (!viewMorePrevious || !viewMoreNext) return;

    this.infinityScrollObserver = new IntersectionObserver(
      async (entries) => {
        if (viewTransition.current) await viewTransition.current;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (entry.target === viewMorePrevious) this.#renderPreviousPage();
            else this.#renderNextPage();
          }
        }
      },
      { rootMargin: '100px' }
    );

    this.infinityScrollObserver.observe(viewMorePrevious);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;
    if (pageInfo.page < 1) return false;

    const { grid } = this.refs;
    const lastPage = grid ? Number(grid.getAttribute('last-page')) : 0;
    if (lastPage > 0 && pageInfo.page > lastPage) return false;

    return true;
  }

  async #fetchPage(type) {
    const page = this.#getPage(type);
    if (!page || !this.#shouldUsePage(page)) return;

    await this.#fetchSpecificPage(page.page, page.url);

    if (type === 'next') {
      this.#resolveNextPagePromise?.();
      this.#resolveNextPagePromise = null;
    } else {
      this.#resolvePreviousPagePromise?.();
      this.#resolvePreviousPagePromise = null;
    }
  }

  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => { this.#resolveNextPagePromise = res; });
      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);
    this.#processNewElements();
    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => { this.#fetchPage('next'); });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;
    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;
    let previousPageItemElements = this.#getGridForPage(previousPage.page);

    if (!previousPageItemElements) {
      const promise = new Promise((res) => { this.#resolvePreviousPagePromise = res; });
      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    grid.prepend(...previousPageItemElements);
    this.#processNewElements();
    history.pushState('', '', previousPage.url.toString());

    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({ top: scrollTop + heightDiff, behavior: 'instant' });
    }

    requestIdleCallback(() => { this.#fetchPage('previous'); });
  }

  #processNewElements() {
    requestAnimationFrame(() => {
      this.#imageRatioSetting === 'adapt' ? this.#fixAdaptiveAspectRatios() : this.#applyFixedAspectRatio();
    });
  }

  #getUnprocessedGalleries() {
    return this.querySelectorAll('.card-gallery:not([data-aspect-ratio-applied])');
  }

  #markAsProcessed(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.setAttribute('data-aspect-ratio-applied', 'true');
  }

  #getSafeImageAspectRatio(width, height) {
    const rawRatio = width / height;
    return Math.max(0.1, Math.min(10, rawRatio)).toFixed(3);
  }

  #applyAspectRatioToGallery(gallery, aspectRatio) {
    if (!(gallery instanceof HTMLElement)) return;
    gallery.style.setProperty('--gallery-aspect-ratio', aspectRatio);

    const mediaContainers = gallery.querySelectorAll('.product-media-container');
    mediaContainers.forEach((container) => {
      if (container instanceof HTMLElement) container.style.aspectRatio = aspectRatio;
    });

    this.#markAsProcessed(gallery);
  }

  #fixAdaptiveAspectRatios() {
    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    const productRatioCache = new Map();

    newCardGalleries.forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      const productId = gallery.getAttribute('data-product-id');
      if (productId && productRatioCache.has(productId)) {
        this.#applyAspectRatioToGallery(gallery, productRatioCache.get(productId));
        return;
      }

      const img = gallery.querySelector('img');
      if (!img) {
        this.#applyAspectRatioToGallery(gallery, '1');
        return;
      }

      const loadAndSetRatio = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;
        const imgRatio = this.#getSafeImageAspectRatio(img.naturalWidth, img.naturalHeight);
        if (productId) productRatioCache.set(productId, imgRatio);
        this.#applyAspectRatioToGallery(gallery, imgRatio);
      };

      if (img.complete) loadAndSetRatio();
      else img.addEventListener('load', loadAndSetRatio, { once: true });
    });
  }

  #applyFixedAspectRatio() {
    if (!this.#imageRatioSetting) return;
    const aspectRatio = this.#getAspectRatioValue(this.#imageRatioSetting);
    if (!aspectRatio) return;

    const newCardGalleries = this.#getUnprocessedGalleries();
    if (!newCardGalleries.length) return;

    requestAnimationFrame(() => {
      newCardGalleries.forEach((gallery) => {
        if (!(gallery instanceof HTMLElement)) return;
        this.#applyAspectRatioToGallery(gallery, aspectRatio);
      });
    });
  }

  static ASPECT_RATIOS = { square: '1', portrait: '0.8', landscape: '1.778' };

  #getAspectRatioValue(ratioSetting) {
    return PaginatedList.ASPECT_RATIOS[ratioSetting] || null;
  }

  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';
    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];
    if (!targetCard) return;

    const page = isPrevious ? Number(targetCard.dataset.page) - 1 : Number(targetCard.dataset.page) + 1;
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return { page, url };
  }

  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();
    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;
    this.#fetchSpecificPage(2);
  };
}

